import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { ArrowLeft, Edit, Download, Maximize, Minimize, Link as LinkIcon, File } from 'lucide-react';
import toast from 'react-hot-toast';
import { api, API_BASE_URL } from '../../api';
import ReactPlayer from 'react-player';
import Plyr from 'plyr';
import Hls from 'hls.js';
import 'plyr/dist/plyr.css';

interface Module {
  _id: string;
  title: string;
  type: 'video' | 'pdf' | 'text' | 'link' | 'image' | 'document';
  content?: string;
  fileUrl?: string;
  externalUrl?: string;
  duration?: number;
  description?: string;
  thumbnailUrl?: string;
}

const AdminCourseModuleViewer: React.FC = () => {
  // Hooks et état
  const { courseId, sectionId, moduleId } = useParams<{
    courseId: string;
    sectionId: string;
    moduleId: string;
  }>();
  const navigate = useNavigate();
  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Références
  const playerRef = useRef<HTMLVideoElement>(null);
  const plyrInstance = useRef<Plyr | null>(null);
  const hlsInstance = useRef<Hls | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch module data
  const fetchModule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/courses/${courseId}/modules/${moduleId}`);
      
      if (!response.data?.data) {
        throw new Error('Module data not found');
      }
      
      setModule(response.data.data);
    } catch (err) {
      console.error('Failed to fetch module:', err);
      setError('Failed to load module');
      toast.error('Failed to load module');
      navigate(`/admin/courses/${courseId}`);
    } finally {
      setLoading(false);
    }
  }, [courseId, moduleId, navigate]);

  useEffect(() => {
    fetchModule();
  }, [fetchModule]);

  // Initialize video player
  useEffect(() => {
    if (!module || module.type !== 'video' || !playerRef.current) return;

    const initializeVideoPlayer = () => {
      const contentUrl = getContentUrl();
      
      // Cleanup previous instances
      if (plyrInstance.current) {
        plyrInstance.current.destroy();
      }
      if (hlsInstance.current) {
        hlsInstance.current.destroy();
      }

      // Handle HLS streams
      if (contentUrl.includes('.m3u8')) {
        if (Hls.isSupported()) {
          hlsInstance.current = new Hls({
            enableWorker: true,
            lowLatencyMode: true,
            backBufferLength: 30
          });
          hlsInstance.current.loadSource(contentUrl);
          hlsInstance.current.attachMedia(playerRef.current!);
          hlsInstance.current.on(Hls.Events.MANIFEST_PARSED, () => {
            playerRef.current?.play().catch(e => console.log('Autoplay prevented:', e));
          });
        } else if (playerRef.current?.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari native HLS support
          playerRef.current!.src = contentUrl;
        }
      } else {
        // Regular video files
        playerRef.current!.src = contentUrl;
      }

      // Initialize Plyr
      plyrInstance.current = new Plyr(playerRef.current!, {
        controls: [
          'play-large',
          'play',
          'progress',
          'current-time',
          'mute',
          'volume',
          'captions',
          'settings',
          'pip',
          'airplay',
          'fullscreen'
        ],
        ratio: '16:9',
        keyboard: { focused: true, global: true },
        tooltips: { controls: true, seek: true },
        storage: { enabled: true, key: 'plyr_volume' }
      });

      // Handle fullscreen changes
      const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
      };
      
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      
      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        if (plyrInstance.current) {
          plyrInstance.current.destroy();
        }
        if (hlsInstance.current) {
          hlsInstance.current.destroy();
        }
      };
    };

    initializeVideoPlayer();
  }, [module]);

  // Helper functions
  const getContentUrl = useCallback(() => {
    if (!module) return '';
    if (module.type === 'link') return module.externalUrl || '';
    if (module.fileUrl) return `${API_BASE_URL}${module.fileUrl}`;
    return module.content || '';
  }, [module]);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Component rendering
  const renderVideoPlayer = useCallback(() => {
    if (!module) return null;
    
    const contentUrl = getContentUrl();
    const isExternalVideo = /youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com/.test(contentUrl);

    return (
      <div 
        ref={containerRef}
        className="relative bg-black rounded-xl overflow-hidden shadow-lg"
      >
        {isExternalVideo ? (
          <ReactPlayer
            src={contentUrl}
            width="100%"
            height="100%"
            controls
            light={module.thumbnailUrl || true}
            config={{
              youtube: {
                playerVars: {
                  modestbranding: 1,
                  rel: 0,
                  showinfo: 0,
                  controls: 1
                }
              },
              vimeo: {
                playerOptions: {
                  byline: false,
                  portrait: false,
                  title: false,
                  color: '00adef'
                }
              }
            } as unknown as React.ComponentProps<typeof ReactPlayer>['config']}
            className="react-player"
          />
        ) : (
          <video
            ref={playerRef}
            className="plyr__video w-full h-full"
            playsInline
            controls
            poster={module.thumbnailUrl}
          />
        )}
        
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-gray-800 bg-opacity-70 rounded-full text-white hover:bg-opacity-100 transition-all"
            aria-label="Toggle fullscreen"
          >
            {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
          </button>
        </div>
      </div>
    );
  }, [module, getContentUrl, isFullscreen, toggleFullscreen]);

  // Fix 18/09 : consultation page par page via /api/pdfpages (rendu serveur PNG, aucune URL PDF exposée)
  const renderPdfViewer = useCallback(() => {
    if (!module) return null;

    const PdfPages = () => {
      const [pages, setPages] = useState<number | null>(null);
      const [err, setErr] = useState('');
      useEffect(() => {
        fetch(`${API_BASE_URL}/api/pdfpages/${module._id}/count`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
        })
          .then(r => r.json())
          .then(d => { if (d.success) setPages(d.pages); else setErr(d.message || 'Erreur de chargement'); })
          .catch(() => setErr('Erreur de chargement du PDF'));
      }, [module._id]);
      if (err) return <div className="text-red-600 py-8">{err}</div>;
      if (pages === null) return <div className="text-gray-500 py-8">Chargement du PDF...</div>;
      const token = encodeURIComponent(localStorage.getItem('token') || '');
      return (
        <div className="w-full max-w-4xl overflow-auto space-y-3" style={{ maxHeight: '75vh' }}>
          {Array.from({ length: pages }, (_, i) => i + 1).map(n => (
            <img
              key={n}
              src={`${API_BASE_URL}/api/pdfpages/${module._id}/pages/${n}?token=${token}`}
              alt={`Page ${n}`}
              className="w-full border rounded shadow-sm bg-white"
              loading="lazy"
            />
          ))}
        </div>
      );
    };

    return (
      <div className="flex flex-col items-center bg-white rounded-xl shadow-sm p-4">
        <h2 className="text-lg font-bold text-gray-900 mb-3">{module.title}</h2>
        <PdfPages />
      </div>
    );
  }, [module]);

  const renderContent = useCallback(() => {
    if (!module) return null;
    
    switch (module.type) {
      case 'video':
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">{module.title}</h2>
            <div className="aspect-w-16 aspect-h-9">
              {renderVideoPlayer()}
            </div>
          </div>
        );
        
      case 'pdf':
        return renderPdfViewer();
        
      case 'text':
        return (
          <div className="prose max-w-none bg-white p-6 rounded-xl shadow-sm">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">{module.title}</h2>
            <div className="text-gray-700 whitespace-pre-wrap">
              {module.content}
            </div>
          </div>
        );
        
      // Fix 18/09/2026 : cas manquants (document, image, link) — aligné sur Student/ModulePlayer.tsx
      case 'image':
        return (
          <div className="flex justify-center items-center p-6">
            <img
              src={getContentUrl()}
              alt={module.title}
              className="max-w-full h-auto max-h-[70vh] rounded-lg shadow-md"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder-image.png';
              }}
            />
          </div>
        );

      case 'link': {
        const linkUrl = module.externalUrl || '';
        const isExternalVideo = /youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com/.test(linkUrl);
        if (isExternalVideo) {
          return (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">{module.title}</h2>
              <div className="aspect-w-16 aspect-h-9">
                <ReactPlayer src={linkUrl} width="100%" height="100%" controls className="react-player" />
              </div>
            </div>
          );
        }
        return (
          <div className="flex flex-col items-center justify-center p-6 min-h-[300px]">
            <div className="text-center">
              <LinkIcon className="h-16 w-16 mx-auto text-[#FFB639]" />
              <h3 className="mt-4 text-lg font-medium">{module.title}</h3>
              <p className="mt-2 text-gray-500 break-all">{linkUrl}</p>
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#FFB639] hover:bg-[#e5a333] transition-colors"
              >
                Ouvrir le lien externe
              </a>
            </div>
          </div>
        );
      }

      case 'document': {
        const docUrl = module.fileUrl ? `${API_BASE_URL}${module.fileUrl}` : '';
        const isOfficeDocument = /\.(docx?|xlsx?|pptx?)$/i.test(docUrl);

        if (!docUrl && !module.content) {
          // État propre pour un module document sans contenu ni fichier
          return (
            <div className="flex flex-col items-center justify-center p-6 min-h-[300px]">
              <div className="text-center">
                <File className="h-16 w-16 mx-auto text-gray-400" />
                <h3 className="mt-4 text-lg font-medium">{module.title}</h3>
                <p className="mt-2 text-gray-500">Ce module n'a pas encore de contenu.</p>
              </div>
            </div>
          );
        }

        if (isOfficeDocument) {
          const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(docUrl)}&embedded=true`;
          return (
            <div className="w-full h-[80vh] p-2">
              <iframe
                src={viewerUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                title={module.title}
                className="border rounded-lg shadow-inner"
              ></iframe>
            </div>
          );
        }

        // Fix 18/09 consultation : un document PDF s'affiche inline (meme rendu que case 'pdf')
        const isPdfDocument = /\.pdf(\?|#|$)/i.test(docUrl) || module.contentType === 'application/pdf';
        if (isPdfDocument && docUrl) {
          return renderPdfViewer();
        }

        return (
          <div className="flex flex-col items-center justify-center p-6 min-h-[300px]">
            <div className="text-center">
              <File className="h-16 w-16 mx-auto text-gray-400" />
              <h3 className="mt-4 text-lg font-medium">{module.title}</h3>
              {module.content ? (
                <div className="mt-4 text-gray-700 whitespace-pre-wrap text-left max-w-2xl">{module.content}</div>
              ) : (
                <>
                  <p className="mt-2 text-gray-500">Document {docUrl.split('.').pop()?.toUpperCase()}</p>
                  <a
                    href={docUrl}
                    download
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#FFB639] hover:bg-[#e5a333] transition-colors"
                    rel="noopener noreferrer"
                  >
                    Télécharger le document
                  </a>
                </>
              )}
            </div>
          </div>
        );
      }

      default:
        return (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            Type de module non pris en charge : {module.type}
          </div>
        );
    }
  }, [module, renderVideoPlayer, renderPdfViewer, getContentUrl]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[300px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FFB639]"></div>
        </div>
      </Layout>
    );
  }

  if (error || !module) {
    return (
      <Layout>
        <div className="text-center py-12 space-y-4">
          <h3 className="text-xl font-medium text-gray-900">{error || 'Module non trouvé'}</h3>
          <button
            onClick={() => navigate(`/admin/courses/${courseId}`)}
            className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
          >
            Retour au cours
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <button
            onClick={() => navigate(`/admin/courses/${courseId}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour au cours
          </button>
          
          <button
            onClick={() => navigate(`/admin/courses/${courseId}/section/${sectionId}/module/${moduleId}/edit`)}
            className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
          >
            <Edit className="h-4 w-4 mr-2" />
            Modifier le module
          </button>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {renderContent()}
        </div>
        
        {module.description && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Description</h2>
            <p className="text-gray-700">{module.description}</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminCourseModuleViewer;